import Collapse from "antd/lib/collapse";
import type { FC } from "react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getColumns } from "src/app/features/ApplicationConfigurations/components/appConfigsUtils";
import type {
    AppConfigsFieldEditableItem,
    AppConfigsTab,
    SubEntity,
} from "src/app/features/ApplicationConfigurations/types/AppConfigsTypes";
import { EditableList } from "../../../../../components/EditableList/EditableList";
import { EditableListRemoveStrategy } from "../../../../../components/EditableList/types/EditableListRemoveStrategy";
import type { AntCustomFormField, CommonFieldAttributes } from "../../../../../components/Form/types/types";
import { deepClone } from "../../../../../core/utils/utilFunctions";
import { SubEntityEditableList } from "./SubEntityEditableList";

export interface ApplicationConfigurationsTabProps extends AntCustomFormField<string>, CommonFieldAttributes {
    appConfigsTab: AppConfigsTab;
    onChangeCallback: Function;
}

export const ApplicationConfigurationsTab: FC<ApplicationConfigurationsTabProps> = ({ appConfigsTab, onChangeCallback }) => {
    const { t } = useTranslation();
    const { Panel } = Collapse;
    const fields = appConfigsTab.configFields;

    // Configure column config
    const columnConfig = getColumns();

    const calculateSubEntityReferenceObject = useCallback((appConfigFields: AppConfigsFieldEditableItem[]) => {
        const subEntityArray = appConfigFields?.filter((field) => field?.reference !== undefined);

        return subEntityArray.map((subEntityItem) => {
            const reference = subEntityItem?.reference;
            return {
                reference,
                key: reference,
                subEntityFields: [],
            } as SubEntity;
        });
    }, []);

    const subEntityReferenceObject = useMemo(
        () => calculateSubEntityReferenceObject(fields || []),
        [calculateSubEntityReferenceObject, fields],
    );

    const onChangeCallbackFields = (fieldsData: AppConfigsFieldEditableItem[]) => {
        if (!appConfigsTab) return;

        const tab = appConfigsTab;
        tab.configFields = fieldsData;

        if (onChangeCallback) {
            onChangeCallback(tab);
        }
    };

    const onChangeCallbackSubEntity = (onChangeData: SubEntity) => {
        const tab = appConfigsTab;

        const cloneSubEntData = deepClone(subEntityReferenceObject);

        const newSubEntityDataIndex = subEntityReferenceObject.findIndex((item) => item.reference === onChangeData.reference);
        if (newSubEntityDataIndex > -1) {
            cloneSubEntData[newSubEntityDataIndex] = onChangeData;
        }

        tab.subEntity = cloneSubEntData;
        if (onChangeCallback) {
            onChangeCallback(tab);
        }
    };

    const renderSubEntities = (data: SubEntity[]) => {
        return data.map((item) => {
            if (!item.reference || !item.key) return [];
            return (
                <Collapse key={`${item.key}-subentityCollapse`}>
                    <Panel header={t(item.reference)} key={`${item.key}-panel`}>
                        <SubEntityEditableList
                            key={`${item.key}-SubEntityEditableList`}
                            subEntityData={item}
                            onChangeCallback={onChangeCallbackSubEntity}
                            columnConfig={columnConfig}
                            removeItems={EditableListRemoveStrategy.ONLY_NEW}
                        />
                    </Panel>
                </Collapse>
            );
        });
    };

    return (
        (onChangeCallbackFields && fields && (
            <>
                <EditableList<AppConfigsFieldEditableItem>
                    value={fields}
                    onChange={onChangeCallbackFields}
                    columns={columnConfig}
                    removeItems={EditableListRemoveStrategy.ONLY_NEW}
                    addItems={false}
                    isFetching={false}
                    name={`${appConfigsTab.key}`}
                />
                {subEntityReferenceObject[0] && renderSubEntities(subEntityReferenceObject)}
            </>
        )) ||
        null
    );
};
